import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { STORAGE_KEYS } from "../../utils/constants";
import { redirectToLogin } from "../../utils/apiClient";
import axios from "axios";
import {
  ShoppingCartIcon,
  CheckCircleIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ClockIcon,
  ExclamationCircleIcon,
  StarIcon,
  ShieldCheckIcon,
  SparklesIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";

// Image Cache
class ImageCache {
  constructor() {
    this.cache = new Map();
    this.failed = new Set();
  }
  markFailed(src) {
    this.failed.add(src);
  }
  preload(src) {
    if (!src || this.cache.has(src) || this.failed.has(src)) return Promise.resolve(src);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        this.cache.set(src, src);
        resolve(src);
      };
      img.onerror = () => {
        this.failed.add(src);
        reject(new Error("Failed"));
      };
      img.src = src;
    });
  }
}
const imageCache = new ImageCache();

const OptimizedImage = ({ src, alt, className, fallbackSrc = "/images/default-bike.png" }) => {
  const [imageSrc, setImageSrc] = useState(src || fallbackSrc);

  useEffect(() => {
    setImageSrc(src || fallbackSrc);
  }, [src, fallbackSrc]);

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      loading="lazy"
      onError={(e) => {
        e.currentTarget.onerror = null;
        imageCache.markFailed(imageSrc);
        setImageSrc(fallbackSrc);
      }}
    />
  );
};

const ServicePackagesPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const BASE_URL = (import.meta.env.VITE_BASE_URL || "http://localhost:8080").replace(/\/+$/, "");

  const [bikeData, setBikeData] = useState({
    brand: "Unknown",
    model: "Unknown",
    image: "/images/default-bike.png",
    brandLogo: "",
    brandId: null,
    modelId: null,
  });

  const [allServices, setAllServices] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState("General Services");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [servicesLoaded, setServicesLoaded] = useState(false);
  const [showCartSidebar, setShowCartSidebar] = useState(false);

  const abortControllerRef = useRef(null);
  const isFetchingRef = useRef(false);

  const serviceCategories = useMemo(
    () => [
      {
        id: 1,
        name: "General Services",
        icon: "🔧",
        color: "indigo",
        description: "Regular maintenance",
      },
      {
        id: 2,
        name: "Bike Repairing",
        icon: "⚙️",
        color: "purple",
        description: "Expert repairs",
      },
    ],
    []
  );

  const normalizeUploadPath = useCallback((p) => {
    if (!p) return "";
    return p.replaceAll("\\\\", "/").replaceAll("\\", "/");
  }, []);

  const buildImageUrl = useCallback(
    (p) => {
      if (!p) return "/images/default-bike.png";
      if (p.startsWith("http")) return p;
      const cleaned = normalizeUploadPath(p).replace(/^\/+/, "");
      return `${BASE_URL}/${cleaned}`;
    },
    [BASE_URL, normalizeUploadPath]
  );

  const formatServicesData = useCallback(
    (services) => {
      if (!Array.isArray(services)) return [];
      return services.map((s, idx) => {
        const img = s.service_image ? buildImageUrl(s.service_image) : "/images/default-bike.png";
        if (img && img !== "/images/default-bike.png") imageCache.preload(img).catch(() => {});
        return {
          id: s.service_id ?? `service-${idx + 1}`,
          name: s.name || `Service ${idx + 1}`,
          originalPrice: Number(s.price) || 0,
          discountedPrice: Number(s.price) || 0,
          image: img,
          category: s.type || "General Services",
          duration: s.duration || "2-3 hours",
          rating: 4.8,
          reviews: 156,
        };
      });
    },
    [buildImageUrl]
  );

  const fetchAllServices = useCallback(async () => {
    if (!bikeData.brandId || !bikeData.modelId) {
      setError("Brand or model information is missing.");
      return;
    }

    if (isFetchingRef.current && abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (abortControllerRef.current) abortControllerRef.current.abort();

    const controller = new AbortController();
    abortControllerRef.current = controller;
    isFetchingRef.current = true;

    try {
      setLoading(true);
      setError(null);

      const res = await axios.get(`${BASE_URL}/admin/service/search`, {
        params: { brand_id: bikeData.brandId, model_id: bikeData.modelId },
        headers: { Accept: "application/json" },
        timeout: 15000,
        signal: controller.signal,
      });

      const raw = Array.isArray(res.data) ? res.data : res.data?.services || [];
      const formatted = formatServicesData(raw);

      setAllServices(formatted);
      setServicesLoaded(true);

      const types = Array.from(new Set(formatted.map((x) => x.category).filter(Boolean)));
      if (types.length && !types.includes(activeCategory)) setActiveCategory(types[0]);
    } catch (err) {
      if (err.name === "AbortError") return;
      setError(err?.response?.data?.message || "Failed to load services. Please try again.");
      setAllServices([]);
      setServicesLoaded(false);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [BASE_URL, bikeData.brandId, bikeData.modelId, formatServicesData, activeCategory]);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!state || !state.bikeData) return;

    const bikeInfo = state.bikeData;

    if (!bikeInfo.brandId || !bikeInfo.modelId) {
      setError("Invalid bike selection. Missing brand or model information.");
      return;
    }

    setBikeData({
      brand: bikeInfo.brand || "Unknown",
      model: bikeInfo.model || "Unknown",
      image: bikeInfo.image || "/images/default-bike.png",
      brandLogo: bikeInfo.brandLogo || "",
      brandId: bikeInfo.brandId,
      modelId: bikeInfo.modelId,
    });

    if (Array.isArray(bikeInfo.services) && bikeInfo.services.length > 0) {
      const formatted = formatServicesData(bikeInfo.services);
      setAllServices(formatted);
      setServicesLoaded(true);
      setError(null);

      const types = Array.from(new Set(formatted.map((x) => x.category).filter(Boolean)));
      if (types.length) setActiveCategory(types.includes(activeCategory) ? activeCategory : types[0]);
    } else {
      setServicesLoaded(false);
    }
  }, [state, formatServicesData]);

  useEffect(() => {
    if (bikeData.brandId && bikeData.modelId && !servicesLoaded) {
      fetchAllServices();
    }
  }, [bikeData.brandId, bikeData.modelId, servicesLoaded, fetchAllServices]);


  // ✅ Restore pending checkout state after returning from login
useEffect(() => {
  const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
  if (!token) return;

  const saved = sessionStorage.getItem("pending_checkout");
  if (!saved) return;

  try {
    const { cartItems: savedCart, bikeData: savedBike, savedAt } = JSON.parse(saved);

    // Ignore if saved more than 30 minutes ago
    if (Date.now() - savedAt > 30 * 60 * 1000) {
      sessionStorage.removeItem("pending_checkout");
      return;
    }

    // Restore cart and bike data
    if (savedCart?.length > 0) setCartItems(savedCart);
    if (savedBike?.brandId) {
      setBikeData(savedBike);
    }

    // Clean up
    sessionStorage.removeItem("pending_checkout");

    // Auto-proceed to checkout if cart was non-empty
    if (savedCart?.length > 0 && savedBike?.brandId) {
      const subtotalRestored = savedCart.reduce(
        (sum, item) => sum + (Number(item.discountedPrice) || 0), 0
      );
      navigate("/checkout", {
        state: {
          cartItems: savedCart,
          bikeData: savedBike,
          store: "Hinjewadi",
          subtotal: subtotalRestored,
        },
      });
    }
  } catch (e) {
    sessionStorage.removeItem("pending_checkout");
  }
}, []); // ✅ runs once on mount — after returning from login

  const toggleCartItem = useCallback((item) => {
    setCartItems((prev) => {
      const exists = prev.some((x) => x.id === item.id);
      return exists ? prev.filter((x) => x.id !== item.id) : [...prev, item];
    });
  }, []);

  const isInCart = useCallback((id) => cartItems.some((x) => x.id === id), [cartItems]);

  const subtotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + (Number(item.discountedPrice) || 0), 0),
    [cartItems]
  );

  const filteredServices = useMemo(() => {
    return allServices.filter((s) => s.category === activeCategory);
  }, [allServices, activeCategory]);

const handleCheckout = useCallback(() => {
  if (cartItems.length === 0) {
    alert("Please select at least one service");
    return;
  }

  const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
  if (!token) {
    // ✅ Save cart + bike state to sessionStorage before redirecting
    const pendingCheckout = {
      cartItems,
      bikeData,
      subtotal,
      savedAt: Date.now(),
    };
    sessionStorage.setItem("pending_checkout", JSON.stringify(pendingCheckout));

    // ✅ Redirect to login with returnUrl pointing back to this page
    const returnUrl = encodeURIComponent("/service-packages");
    navigate(`/login?returnUrl=${returnUrl}`, {
      state: {
        message: "Please login to proceed with checkout",
        returnUrl: "/service-packages",
      },
    });
    return;
  }

  navigate("/checkout", {
    state: { cartItems, bikeData, store: "Hinjewadi", subtotal },
  });
}, [cartItems, bikeData, navigate, subtotal]);

  if (!state || !state.bikeData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-3xl shadow-2xl text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExclamationCircleIcon className="h-10 w-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">No Bike Selected</h2>
          <p className="text-gray-600 mb-6">
            Please select a bike to view available services.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-semibold shadow-lg"
          >
            Select Your Bike
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 pb-24 lg:pb-8">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-indigo-800 via-indigo-700 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center hover:bg-white/30 transition-all"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <div className="flex-1">
              <p className="text-indigo-100 text-sm mb-1">Servicing for</p>
              <h1 className="text-2xl sm:text-3xl font-bold">
                {bikeData.brand} {bikeData.model}
              </h1>
            </div>
            <button
              onClick={() => setShowCartSidebar(true)}
              className="lg:hidden relative w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center hover:bg-white/30 transition-all"
            >
              <ShoppingCartIcon className="h-6 w-6" />
              {cartItems.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {cartItems.length}
                </span>
              )}
            </button>
          </div>

          {/* Trust Badges */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <ShieldCheckIcon className="w-4 h-4" />
              <span>100% Safe</span>
            </div>
            <div className="flex items-center gap-1">
              <StarSolidIcon className="w-4 h-4 text-yellow-400" />
              <span>4.8 Rating</span>
            </div>
            <div className="flex items-center gap-1">
              <CheckCircleIcon className="w-4 h-4" />
              <span>Certified</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 lg:py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main Content */}
          <div className="flex-1">
            {/* Category Pills */}
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Choose Service Type</h2>
              <div className="grid grid-cols-2 gap-3">
                {serviceCategories.map((category) => (
                  <button
                    key={category.id}
                    className={`relative p-4 rounded-2xl border-2 transition-all ${
                      activeCategory === category.name
                        ? "border-indigo-600 bg-indigo-50 shadow-lg scale-105"
                        : "border-gray-200 bg-white hover:border-indigo-300 hover:shadow-md"
                    }`}
                    onClick={() => {
                      setError(null);
                      setActiveCategory(category.name);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`text-3xl ${activeCategory === category.name ? "scale-110" : ""} transition-transform`}>
                        {category.icon}
                      </div>
                      <div className="text-left flex-1">
                        <h3 className={`font-bold text-sm mb-0.5 ${
                          activeCategory === category.name ? "text-indigo-600" : "text-gray-900"
                        }`}>
                          {category.name}
                        </h3>
                        <p className="text-xs text-gray-500">{category.description}</p>
                      </div>
                    </div>
                    {activeCategory === category.name && (
                      <div className="absolute top-2 right-2">
                        <CheckCircleIcon className="h-6 w-6 text-indigo-600" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
                <div className="relative w-16 h-16 mx-auto mb-4">
                  <div className="absolute inset-0 border-4 border-indigo-200 rounded-full"></div>
                  <div className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Loading Services</h3>
                <p className="text-gray-600">Please wait...</p>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="bg-red-50 rounded-3xl shadow-xl p-6 border-2 border-red-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <ExclamationCircleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-red-900 mb-2">Unable to Load Services</h3>
                    <p className="text-red-700 text-sm mb-4">{error}</p>
                    <button
                      onClick={fetchAllServices}
                      className="px-6 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 font-semibold"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && filteredServices.length === 0 && (
              <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SparklesIcon className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No Services Available</h3>
                <p className="text-gray-600">
                  No {activeCategory.toLowerCase()} found for your {bikeData.brand} {bikeData.model}.
                </p>
              </div>
            )}

            {/* Services Grid */}
            {!loading && !error && filteredServices.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold text-gray-900">Available Services</h3>
                  <span className="text-sm text-gray-500">{filteredServices.length} services</span>
                </div>
                {filteredServices.map((pkg) => (
                  <ServiceCard key={pkg.id} pkg={pkg} isInCart={isInCart} toggleCartItem={toggleCartItem} />
                ))}
              </div>
            )}
          </div>

          {/* Desktop Cart */}
          <DesktopCart
            bikeData={bikeData}
            cartItems={cartItems}
            subtotal={subtotal}
            toggleCartItem={toggleCartItem}
            handleCheckout={handleCheckout}
          />
        </div>
      </div>

      {/* Mobile Cart Sidebar */}
      <MobileCartSidebar
        show={showCartSidebar}
        onClose={() => setShowCartSidebar(false)}
        bikeData={bikeData}
        cartItems={cartItems}
        subtotal={subtotal}
        toggleCartItem={toggleCartItem}
        handleCheckout={handleCheckout}
      />

      {/* Mobile Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-30 shadow-2xl">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-0.5">
              {cartItems.length} service{cartItems.length !== 1 ? "s" : ""} selected
            </p>
            <p className="text-2xl font-bold text-gray-900">₹{subtotal}</p>
          </div>
          <button
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${
              cartItems.length === 0
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg"
            }`}
            onClick={handleCheckout}
            disabled={cartItems.length === 0}
          >
            <span>{cartItems.length === 0 ? "Select Services" : "Checkout"}</span>
            <ArrowRightIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Modern Service Card
const ServiceCard = ({ pkg, isInCart, toggleCartItem }) => {
  const savings = (Number(pkg.originalPrice) || 0) - (Number(pkg.discountedPrice) || 0);

  return (
    <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-100 overflow-hidden group">
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Image */}
          <div className="sm:w-40 sm:h-40 flex-shrink-0">
            <OptimizedImage
              src={pkg.image}
              alt={pkg.name}
              className="w-full h-48 sm:h-full object-cover rounded-xl"
            />
          </div>

          {/* Content */}
          <div className="flex-1 space-y-3">
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 group-hover:text-indigo-600 transition-colors">
                {pkg.name}
              </h3>
              
              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 mb-3">
                <div className="flex items-center gap-1">
                  <ClockIcon className="w-4 h-4 text-indigo-600" />
                  <span>{pkg.duration}</span>
                </div>
                <div className="flex items-center gap-1">
                  <StarSolidIcon className="w-4 h-4 text-yellow-400" />
                  <span className="font-semibold text-gray-900">{pkg.rating}</span>
                  <span>({pkg.reviews})</span>
                </div>
              </div>
            </div>

            {/* Price & Action */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-3 border-t border-gray-100">
              <div className="flex items-baseline gap-2">
                {savings > 0 && (
                  <span className="text-gray-400 line-through text-lg">₹{pkg.originalPrice}</span>
                )}
                <span className="text-3xl font-bold text-gray-900">₹{pkg.discountedPrice}</span>
              </div>

              <button
                className={`w-full sm:w-auto px-6 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                  isInCart(pkg.id)
                    ? "bg-green-600 text-white shadow-lg"
                    : "bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg hover:shadow-xl"
                }`}
                onClick={() => toggleCartItem(pkg)}
              >
                {isInCart(pkg.id) ? (
                  <>
                    <CheckCircleIcon className="w-5 h-5" />
                    <span>Added</span>
                  </>
                ) : (
                  <>
                    <ShoppingCartIcon className="w-5 h-5" />
                    <span>Add to Cart</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Desktop Cart Component
const DesktopCart = ({ bikeData, cartItems, subtotal, toggleCartItem, handleCheckout }) => (
  <div className="hidden lg:block w-96">
    <div className="bg-white rounded-3xl shadow-2xl p-6 sticky top-6 border border-gray-100">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-200">
        <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
          <ShoppingCartIcon className="w-6 h-6 text-indigo-600" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900">Your Cart</h3>
          <p className="text-sm text-gray-500">{cartItems.length} items</p>
        </div>
      </div>

      {/* Cart Items */}
      {cartItems.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShoppingCartIcon className="h-8 w-8 text-gray-400" />
          </div>
          <p className="text-gray-600 font-medium mb-1">Cart is empty</p>
          <p className="text-gray-400 text-sm">Add services to get started</p>
        </div>
      ) : (
        <>
          <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
            {cartItems.map((item) => (
              <div key={item.id} className="flex items-start gap-3 p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2 leading-tight">{item.name}</h4>
                  <p className="text-lg font-bold text-indigo-600">₹{item.discountedPrice}</p>
                </div>
                <button
                  onClick={() => toggleCartItem(item)}
                  className="w-8 h-8 bg-red-100 hover:bg-red-200 rounded-lg flex items-center justify-center transition-colors"
                >
                  <XMarkIcon className="w-5 h-5 text-red-600" />
                </button>
              </div>
            ))}
          </div>

          {/* Total */}
          <div className="border-t border-gray-200 pt-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600 font-medium">Total Amount</span>
              <span className="text-3xl font-bold text-gray-900">₹{subtotal}</span>
            </div>

            <button
              className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-bold hover:shadow-2xl transition-all flex items-center justify-center gap-2"
              onClick={handleCheckout}
            >
              <span>Proceed to Checkout</span>
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
        </>
      )}
    </div>
  </div>
);

// Mobile Cart Sidebar
const MobileCartSidebar = ({ show, onClose, bikeData, cartItems, subtotal, toggleCartItem, handleCheckout }) => (
  <>
    {show && (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={onClose}></div>
    )}

    <div
      className={`fixed top-0 right-0 bottom-0 w-full max-w-sm bg-white z-50 transform transition-transform duration-300 shadow-2xl ${
        show ? "translate-x-0" : "translate-x-full"
      }`}
    >
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold">Your Cart</h3>
              <p className="text-indigo-100 text-sm">{cartItems.length} items selected</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center hover:bg-white/30"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {cartItems.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingCartIcon className="h-10 w-10 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium">Your cart is empty</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cartItems.map((item) => (
                <div key={item.id} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">{item.name}</h4>
                    <p className="text-xl font-bold text-indigo-600">₹{item.discountedPrice}</p>
                  </div>
                  <button
                    onClick={() => toggleCartItem(item)}
                    className="w-8 h-8 bg-red-100 hover:bg-red-200 rounded-lg flex items-center justify-center"
                  >
                    <XMarkIcon className="w-5 h-5 text-red-600" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 space-y-4 bg-white">
          <div className="flex justify-between items-center">
            <span className="text-gray-600 font-semibold">Total Amount</span>
            <span className="text-3xl font-bold text-gray-900">₹{subtotal}</span>
          </div>

          <button
            className={`w-full py-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
              cartItems.length === 0
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg"
            }`}
            onClick={() => {
              handleCheckout();
              onClose();
            }}
            disabled={cartItems.length === 0}
          >
            <span>CHECKOUT NOW</span>
            <ArrowRightIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  </>
);

export default ServicePackagesPage;
